import type { ImageProps } from '@mote/ui/components'
import { H2, H3, Image, P } from '@mote/ui/components'
import styled, { th } from '@xstyled/styled-components'
import { Container } from './shared'

type IFeature = {
  title: string
  description: string
  image: Pick<ImageProps, 'src' | 'alt' | 'sizes' | 'srcSet'>
}

type IFeatures = Array<IFeature>

const FEATURES: IFeatures = [
  {
    title: 'Voice to text',
    description:
      'Give recipients the option to read as well as hear your voice notes',
    image: {
      src: 'https://uploads-ssl.webflow.com/62601a2f1ad4af5dee4bd21e/62c5565fc6fda19fc1655eb2_Rocket.png',
      alt: 'Transcribe with Mote'
    }
  },
  {
    title: 'Translation',
    description:
      'Allow recipients to translate your motes, so no-one feels excluded',
    image: {
      src: 'https://uploads-ssl.webflow.com/62601a2f1ad4af5dee4bd21e/62c55760fa5f7e7e261b77c4_Globe.png',
      srcSet:
        'https://uploads-ssl.webflow.com/62601a2f1ad4af5dee4bd21e/62c55760fa5f7e7e261b77c4_Globe-p-500.png 500w, https://uploads-ssl.webflow.com/62601a2f1ad4af5dee4bd21e/62c55760fa5f7e7e261b77c4_Globe.png 600w',
      sizes: '50px',
      alt: 'Translate with Mote'
    }
  },
  {
    title: 'Reply to motes',
    description:
      'Receive audio replies to your motes from learners, parents or colleagues',
    image: {
      src: 'https://uploads-ssl.webflow.com/62601a2f1ad4af5dee4bd21e/62c5a58b420774720c5e5ed4_Bubble.png',
      srcSet:
        'https://uploads-ssl.webflow.com/62601a2f1ad4af5dee4bd21e/62c5a58b420774720c5e5ed4_Bubble-p-500.png 500w, https://uploads-ssl.webflow.com/62601a2f1ad4af5dee4bd21e/62c5a58b420774720c5e5ed4_Bubble.png 600w',
      sizes: '50px',
      alt: 'Reply to a mote'
    }
  },
  {
    title: 'Track engagement',
    description: 'See which of your motes and loops have been seen and heard',
    image: {
      src: 'https://uploads-ssl.webflow.com/62601a2f1ad4af5dee4bd21e/62c5571e09ff9c7f14172198_eyes.png',
      srcSet:
        'https://uploads-ssl.webflow.com/62601a2f1ad4af5dee4bd21e/62c5571e09ff9c7f14172198_eyes-p-500.png 500w, https://uploads-ssl.webflow.com/62601a2f1ad4af5dee4bd21e/62c5571e09ff9c7f14172198_eyes.png 761w',
      sizes: '50px',
      alt: 'Track student engagement'
    }
  },
  {
    title: 'Talk STEM',
    description: 'STEM mode recognizes scientific and math language',
    image: {
      src: 'https://uploads-ssl.webflow.com/62601a2f1ad4af5dee4bd21e/62c5a63508062171fc3492a6_1.png',
      srcSet:
        'https://uploads-ssl.webflow.com/62601a2f1ad4af5dee4bd21e/62c5a63508062171fc3492a6_1-p-500.png 500w, https://uploads-ssl.webflow.com/62601a2f1ad4af5dee4bd21e/62c5a63508062171fc3492a6_1.png 600w',
      sizes: '50px',
      alt: 'Use STEM mode with Mote'
    }
  },
  {
    title: 'React with emojis',
    description:
      'Emoji reactions enable easy acknowledgement of your voice notes',
    image: {
      src: 'https://uploads-ssl.webflow.com/62601a2f1ad4af5dee4bd21e/62c5a5bd04417c66f06c54d7_thumbs%20up%20yellow.png',
      alt: 'React with emojis'
    }
  },
  {
    title: 'Record anywhere!',
    description:
      'Seamless integrations make Mote the fastest & easiest way to record',
    image: {
      src: 'https://uploads-ssl.webflow.com/62601a2f1ad4af5dee4bd21e/62c5a613880c36105dc3f032_check%20mark%20button.png',
      srcSet:
        'https://uploads-ssl.webflow.com/62601a2f1ad4af5dee4bd21e/62c5a613880c36105dc3f032_check%20mark%20button-p-500.png 500w, https://uploads-ssl.webflow.com/62601a2f1ad4af5dee4bd21e/62c5a613880c36105dc3f032_check%20mark%20button.png 531w',
      sizes: '50px',
      alt: 'Record across the web'
    }
  }
]

const Feature = (props: IFeature): JSX.Element => (
  <FeatureRoot>
    <FeatureImageContainer>
      <FeatureImage {...props.image} loading='lazy' />
    </FeatureImageContainer>
    <FeatureContent>
      <FeatureTitle>{props.title}</FeatureTitle>
      <FeatureDescription>{props.description}</FeatureDescription>
    </FeatureContent>
  </FeatureRoot>
)

export const FeaturesSection = (): JSX.Element => {
  return (
    <Root>
      <Title>Key Product Features</Title>
      <FeaturesContainer>
        {FEATURES.map((feature, index) => (
          <Feature {...feature} key={index} />
        ))}
      </FeaturesContainer>
    </Root>
  )
}

const Root = styled(Container)`
  gap: 0;
  padding-top: 80px;
  padding-bottom: 120px;

  @media (max-width: 991px) {
    padding-top: 40px;
    padding-bottom: 40px;
  }
`

const Title = styled(H2).attrs({
  alignment: 'center',
  lineHeight: '123.5%',
  fontWeight: 400,
  color: 'black'
})`
  width: 80%;
  font-size: 39px;

  @media (max-width: 991px) {
    font-size: 32px;
  }
`

const FeaturesContainer = styled.div`
  display: grid;
  width: 85%;
  max-width: 1200px;
  padding-top: 80px;
  grid-column-gap: 20px;
  grid-row-gap: 40px;
  grid-template-columns: repeat(4, minmax(0, 1fr));

  @media (max-width: 991px) {
    padding-top: 40px;
    grid-column-gap: 40px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 767px) {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
`

const FeatureRoot = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 40px 32px 32px;
  border-radius: 12px;
  background-color: ${th.color('grey.l4')};
`

const FeatureContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (max-width: 991px) {
    align-items: flex-start;
    justify-content: flex-start;
  }
`

const FeatureImageContainer = styled.div`
  display: flex;
  align-items: center;
  width: 50px;
  height: 50px;
  padding-bottom: 10px;
`

const FeatureImage = styled(Image)`
  max-width: 100%;
  vertical-align: middle;
`

const FeatureTitle = styled(H3).attrs({
  color: 'black',
  fontSize: 24,
  lineHeight: '135%',
  fontWeight: 400
})`
  margin-top: 20px;
  margin-bottom: 10px;
`

const FeatureDescription = styled(P).attrs({
  color: 'grey.d2',
  lineHeight: '150%'
})`
  margin-bottom: 0;
  padding-top: 16px;
  padding-bottom: 16px;
  font-size: 18px;
  max-width: 400px;

  @media (max-width: 991px) {
    max-width: 600px;
  }
`
